import React, { useEffect } from 'react';
import Box from '@mui/material/Box';
import useVoyagerStore from '../stores/useVoyagerStore';
import MainView from './MainView';
import HomeView from './HomeView';
import ThreadView from './ThreadView';
import ReviewDoc from '../components/Ladder/ReviewDoc';
import AgentChat from '../components/Agents/AgentChat';
import GroupTab from '../components/Agents/GroupTab';
import ObjectiveSpace from './ObjectiveSpace';
import CloseUndoToast from './CloseUndoToast';
import SimpleShell from './SimpleShell';
import SimplePlaceholderPage from './SimplePlaceholder';
import ActionItemsPage from './ActionItemsPage';
import ImpactPage from '../components/Impact/ImpactPage';
import { useSimpleSidebar } from './simpleSidebarState';

/**
 * The Ladder shell (#182): the simplified app's one sidebar (#1500, `SimpleShell`)
 * and a view host. `landing` shows Home when no thread is current and the current
 * thread's view otherwise. `/` is the New-chat screen.
 */
export default function LadderShell() {
  const currentThreadId = useVoyagerStore((s) => s.currentThreadId);
  const currentIsDoc = useVoyagerStore((s) => s.savedThreads.find((t) => t.id === s.currentThreadId)?.kind === 'doc');
  const currentIsAgentChat = useVoyagerStore((s) => s.savedThreads.find((t) => t.id === s.currentThreadId)?.kind === 'agent_chat');
  const currentIsGroup = useVoyagerStore((s) => s.savedThreads.find((t) => t.id === s.currentThreadId)?.kind === 'group');
  const hasTurns = useVoyagerStore((s) => s.landingAgentTurns.length > 0);
  const objectiveSpaceId = useVoyagerStore((s) => s.objectiveSpaceId);
  const fetchLandingAgentPerspectives = useVoyagerStore((s) => s.fetchLandingAgentPerspectives);
  const perspectives = useVoyagerStore((s) => s.perspectivesList);
  const ensureFeatures = useVoyagerStore((s) => s.ensureFeatures);
  const workspaceGeneration = useVoyagerStore((s) => s.threadsGeneration);
  // a destination of the one sidebar whose page is not built yet (#1503)
  const placeholder = useSimpleSidebar((s) => s.placeholder);

  // read again after a workspace switch resets the flags (the read is shared with every other feature reader)
  useEffect(() => { void ensureFeatures(); }, [ensureFeatures, workspaceGeneration]);

  useEffect(() => {
    if (perspectives.length === 0) fetchLandingAgentPerspectives();
  }, [perspectives.length, fetchLandingAgentPerspectives]);

  // Keyed by thread: switching tabs REMOUNTS the view, so the composer
  // starts from that tab's own draft and the scroll baseline is that tab's —
  // a reused instance would carry the previous tab's text and turn count.
  // A conversation with no thread yet — a `?session=` deep link out of Slack
  // restores an answer before any thread exists — shows as a thread view
  // too; the first follow-up mints its thread and the tab appears.
  // A review document (#189) is a tab too — the doc, not a conversation.
  // So is a chat with an agent (#202): steering, signed and read before its next run.
  // And a group (#307): a router — the member whose objective binds answers in its own name.
  const landing = currentThreadId
    ? (currentIsDoc
      ? <ReviewDoc key={currentThreadId} threadId={currentThreadId} />
      : currentIsAgentChat
        ? <AgentChat key={currentThreadId} threadId={currentThreadId} />
        : currentIsGroup
          ? <GroupTab key={currentThreadId} threadId={currentThreadId} />
          : <ThreadView key={currentThreadId} threadId={currentThreadId} />)
    : hasTurns
      ? <ThreadView key="restored" threadId="restored" />
      : objectiveSpaceId
        ? <ObjectiveSpace key={objectiveSpaceId} id={objectiveSpaceId} />
        : <HomeView />;

  return (
    <Box data-testid="ladder-shell" sx={{ display: 'flex', height: '100vh', overflow: 'hidden', '@media (max-width:860px)': { flexDirection: 'column' } }}>
      {/* #1503: the one sidebar in SimpleShell */}
      <SimpleShell />
      <Box sx={{ flex: 1, display: 'flex', minWidth: 0, minHeight: 0, overflow: 'hidden' }}>
        <Box sx={{ flex: 1, overflow: 'hidden', display: 'flex', flexDirection: 'column', minWidth: 0 }}>
          {placeholder === 'action-items' ? <ActionItemsPage />
            : placeholder === 'impact' ? <ImpactPage />
            : placeholder ? <SimplePlaceholderPage page={placeholder} /> : <MainView landing={landing} />}
        </Box>
      </Box>
      {/* on every page, not in the sidebar: a close's Undo outlives the sidebar unmounting */}
      <CloseUndoToast />
    </Box>
  );
}
